import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { ErrorState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Target {
  id: string;
  deviceId: string;
  status: string;
  batch: number;
  steps: string[];
  error: string | null;
}
interface Deployment {
  id: string;
  status: string;
  paused: boolean;
  plan: { warnings?: string[] } | null;
  targets: Target[];
}

const TERMINAL = ['SUCCEEDED', 'FAILED', 'ROLLED_BACK', 'CANCELLED'];

export function DeploymentDetailPage(): JSX.Element {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const { data, error, loading, reload } = useAsync(
    () => api.get<Deployment>(`/deployments/${id}`),
    [id],
  );

  // Live progress: poll while the deployment is still running.
  useEffect(() => {
    if (!data || TERMINAL.includes(data.status)) return;
    const timer = setInterval(reload, 2000);
    return () => clearInterval(timer);
  }, [data, reload]);

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error.message} onRetry={reload} />;
  if (!data) return <ErrorState message={t('errors.notFound')} />;

  return (
    <section>
      <div className="page-head">
        <h1>
          <Link to="/deployments">{t('deployment.title')}</Link> / {data.id.slice(0, 8)}
        </h1>
        <span className={`chip chip--${data.status.toLowerCase()}`}>{data.status}</span>
      </div>

      {(data.plan?.warnings ?? []).map((w) => (
        <div key={w} className="banner banner--warning" role="alert">
          {w}
        </div>
      ))}

      <div className="card">
        <h2>{t('deployment.targets')}</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Device</th>
                <th>Batch</th>
                <th>Status</th>
                <th>{t('deployment.steps')}</th>
              </tr>
            </thead>
            <tbody>
              {data.targets.map((tg) => (
                <tr key={tg.id}>
                  <td>
                    <Link to={`/devices/${tg.deviceId}`}>
                      <code>{tg.deviceId.slice(0, 8)}</code>
                    </Link>
                  </td>
                  <td>{tg.batch}</td>
                  <td>
                    <span className={`chip chip--${tg.status.toLowerCase()}`}>{tg.status}</span>
                  </td>
                  <td>{(tg.steps ?? []).join(' → ') || (tg.error ?? '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
